import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./DropdownMenu.css";

/**
 * Anchored dropdown menu.
 *
 * Rendered through a portal with fixed positioning because the offer table
 * clips its content (`overflow: hidden`) — an in-flow popover inside a row
 * would be cut off at the row edge.
 *
 * items: [{ id, label, description?, icon?, onSelect, variant?, accent?,
 *           separatorBefore?, active? }]
 *   variant: "default" | "danger"
 *   accent:  optional css colour for the icon (premium highlight)
 */

const MENU_GAP = 6;
const VIEWPORT_PADDING = 12;
// Rough per-row height used to decide whether the menu still fits below the
// trigger. Measuring first would cost a paint and make the menu jump.
const ROW_HEIGHT_ESTIMATE = 46;
const ROW_HEIGHT_WITH_DESCRIPTION = 60;

const DropdownMenu = ({
  items = [],
  triggerContent,
  triggerClassName = "",
  triggerTitle,
  ariaLabel,
  align = "end",
  width = 264,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);

  const estimatedHeight =
    items.reduce(
      (total, item) =>
        total +
        (item.description ? ROW_HEIGHT_WITH_DESCRIPTION : ROW_HEIGHT_ESTIMATE) +
        (item.separatorBefore ? 9 : 0),
      0
    ) + 12;

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const rawLeft = align === "end" ? rect.right - width : rect.left;
    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(rawLeft, window.innerWidth - width - VIEWPORT_PADDING)
    );

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const openUpwards =
      spaceBelow < estimatedHeight && rect.top > window.innerHeight - rect.bottom;

    setPosition({
      left,
      ...(openUpwards
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
      maxHeight: openUpwards
        ? rect.top - MENU_GAP - VIEWPORT_PADDING
        : spaceBelow,
    });
  }, [isOpen, align, width, estimatedHeight]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      close();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    // Scrolling would detach the fixed menu from its trigger, so close it.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen, close]);

  const handleSelect = (event, item) => {
    event.stopPropagation();
    close();
    item.onSelect?.();
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`dropdown-menu__trigger ${triggerClassName} ${isOpen ? "is-open" : ""}`}
        title={triggerTitle}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
      >
        {triggerContent}
      </button>

      {isOpen &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            className="dropdown-menu"
            role="menu"
            style={{ ...position, width }}
            onClick={(event) => event.stopPropagation()}
          >
            {items.map((item) => (
              <React.Fragment key={item.id}>
                {item.separatorBefore && <div className="dropdown-menu__separator" />}
                <button
                  type="button"
                  role="menuitem"
                  className={`dropdown-menu__item ${
                    item.variant === "danger" ? "dropdown-menu__item--danger" : ""
                  } ${item.active ? "is-active" : ""}`}
                  onClick={(event) => handleSelect(event, item)}
                >
                  {item.icon && (
                    <span
                      className="dropdown-menu__item-icon"
                      style={item.accent ? { color: item.accent } : undefined}
                      aria-hidden="true"
                    >
                      <i className={`fas ${item.icon}`}></i>
                    </span>
                  )}
                  <span className="dropdown-menu__item-text">
                    <span className="dropdown-menu__item-label">{item.label}</span>
                    {item.description && (
                      <span className="dropdown-menu__item-description">
                        {item.description}
                      </span>
                    )}
                  </span>
                  {item.active && (
                    <span className="dropdown-menu__item-check" aria-hidden="true">
                      <i className="fas fa-check"></i>
                    </span>
                  )}
                </button>
              </React.Fragment>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default DropdownMenu;
